import {useProductListItems} from '@/framework/product';
import {ProductQueryParams} from '@/lib/constants';
import {SortEnum, SortField} from '__generated__/__types__';
import {ProductListItemsQueryOptions} from '@/_application/modules/products/request/product-list.request';
import {useProductListView} from '@/_application/modules/products/provider/product-list-view.provider';
import {filterAttributeCodeValueType} from '@/framework/static/filter-attribute-code-value-type';
import {useCallback, useEffect, useMemo} from 'react';
import {usePathname, useSearchParams, useRouter} from 'next/navigation';
export interface SortInput {
    sortKey: string;
    direction: SortEnum;
}

// This hook should only be called one time at top of category product list
export const useProductsFilterActions = () => {
    const router = useRouter();
    const pathName = usePathname();
    const query = useSearchParams();

    const {
        filter,
        onFilterChange,
        search,
        sort,
        onSortChange,
        currentPage,
        onCurrentPageChange,
        pageSize,
        language,
        sortFields,
        onSortFieldsChange,
    } = useProductListView();

    const variables: ProductListItemsQueryOptions = useMemo(
        () => ({
            pageSize,
            currentPage,
            sort,
            filter,
            search,
            language,
        }),
        [pageSize, currentPage, sort, filter, search, language],
    );

    const {fetchData, products, isLoading, error, isLoadingMore, hasMore, pageInfo, totalCount} = useProductListItems({
        ...variables,
        ...(query.get('category') && {categories: query.get('category')}),
        ...(query.get('text') && {name: query.get('text')}),
    });

    useEffect(() => {
        const newSortFields = (products?.sort_fields?.options?.filter(option => option) ?? []) as SortField[];
        if (!sortFields?.length && !newSortFields?.length) return;
        onSortFieldsChange(newSortFields);
    }, [products]);

    const handleRemoveQuery = useCallback(
        (type: string) => {
            const params = new URLSearchParams(query.toString());
            params.delete(type);
            router.replace(params.toString() ? `${pathName}?${params.toString()}` : pathName);
        },
        [query, pathName, router],
    );

    const handleUpdateParams = useCallback(
        (attribute: string, value: string) => {
            const params = new URLSearchParams(query.toString());
            params.set(attribute, value);
            router.replace(`${pathName}?${params.toString()}`);
        },
        [router, query, pathName],
    );

    const handleFilterChange = (values: string[] | string, attributeCode: string) => {
        if (!filter) {
            return;
        }

        handleRemoveQuery(ProductQueryParams.PAGINATION);

        if (Array.isArray(values) && values.length === 0) {
            if (Object.keys(filter).includes(attributeCode)) {
                const {[attributeCode]: removeField, ...updateFilter} = filter as any;
                onCurrentPageChange(1);
                onFilterChange(updateFilter);
                handleRemoveQuery(attributeCode);
            }
            return;
        }

        let attributeCodeValue;
        if (typeof values === 'string') {
            attributeCodeValue = filterAttributeCodeValueType.getFilterMatchTypeValue(values);
            handleUpdateParams(attributeCode, values);
        } else if (filterAttributeCodeValueType.filterEqualType.includes(attributeCode)) {
            attributeCodeValue = filterAttributeCodeValueType.getFilterEqualTypeValue(values);
            handleUpdateParams(attributeCode, values.join(','));
        } else if (filterAttributeCodeValueType.filterRangeType.includes(attributeCode)) {
            attributeCodeValue = filterAttributeCodeValueType.getFilterRangeTypeValue(values);
            handleUpdateParams(attributeCode, values.join('-'));
        }
        const filterInput: ProductListItemsQueryOptions['filter'] = {
            ...filter,
            [attributeCode]: {...attributeCodeValue},
        };

        onCurrentPageChange(1);
        onFilterChange(filterInput);
    };

    const handleSortChange = ({sortKey, direction}: SortInput) => {
        const sortUpdated: ProductListItemsQueryOptions['sort'] = {[sortKey]: direction};
        onCurrentPageChange(1);
        onSortChange(sortUpdated);

        // Remove pagination when change the sort
        handleRemoveQuery(ProductQueryParams.PAGINATION);

        // Update query
        handleUpdateParams(ProductQueryParams.SORTING, sortKey);
        handleUpdateParams(ProductQueryParams.DIRECTION, direction);
    };

    const handlePaginationChange = (value: number) => {
        onCurrentPageChange(value);

        if (!value || value === 1) {
            handleRemoveQuery(ProductQueryParams.PAGINATION);
        } else {
            handleUpdateParams(ProductQueryParams.PAGINATION, value.toString());
        }
    };

    return {
        variables,
        fetchData,
        products,
        pageInfo,
        totalCount,
        isLoadingMore,
        isLoading,
        hasMore,
        error,
        handleFilterChange,
        handleSortChange,
        handlePaginationChange,
    };
};
